import { makeStyles } from '@material-ui/styles'
import React, { useCallback } from 'react'
import { VMHAConditionKey } from 'api-client/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { VMHACondition } from './model'

interface Props {
  title: string | React.ReactNode
  conditions?: readonly VMHACondition[]
  className?: string
  height?: number
  loading?: boolean
  plainText?: boolean
  textWithPointers?: boolean
  forTooltip?: boolean
}

const iconByStatus = {
  pass: 'check',
  failed: 'xmark',
  loading: 'circle-notch fa-spin',
  textWithPointers: 'circle',
  degraded: 'exclamation-triangle',
} as const

const VmhaConditionsStatus: React.FC<Props> = ({
  title,
  conditions,
  loading = false,
  plainText = false,
  textWithPointers = false,
  forTooltip = false,
}) => {
  const classes = useStyles()
  const textVariant = plainText || textWithPointers ? 'body2' : 'caption1'
  const iconSize = textWithPointers ? 'sm' : plainText ? 'md' : 'lg'
  const isPlainTextConstainer =
    plainText || textWithPointers ? classes.plainTextContainer : classes.textContainer

  const getIconClass = useCallback(
    (status: string) => {
      switch (status) {
        case 'pass':
          return classes.successIcon
        case 'failed':
          return classes.failedIcon
        case 'degraded':
          return classes.degradedIcon
        case 'textWithPointers':
          return classes.textWithPointersIcon
        case 'loading':
        default:
          return classes.loadingIcon
      }
    },
    [classes],
  )

  if (forTooltip) {
    return (
      <div className={classes.tooltipIconContainer}>
        {conditions?.map((condition) => {
          const status =
            condition.key === VMHAConditionKey.ImageLibrary && condition.degraded
              ? 'degraded'
              : condition.passed
                ? 'pass'
                : 'failed'

          return (
            <div key={condition.key} className={classes.conditionContainer}>
              <FontAwesomeIcon
                className={getIconClass(status)}
                size={iconSize}
                solid={status !== 'degraded'}
              >
                {iconByStatus[status]}
              </FontAwesomeIcon>
              <Text variant="body2">{condition.label}</Text>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className={classes.conditionsGrid}>
      {title && (
        <Text variant="body2" className={classes.title}>
          {title}
        </Text>
      )}

      {conditions?.map((condition) => {
        const status = textWithPointers
          ? 'textWithPointers'
          : loading
            ? 'loading'
            : condition.key === VMHAConditionKey.ImageLibrary && condition.degraded
              ? 'degraded'
              : condition.passed
                ? 'pass'
                : 'failed'

        return (
          <React.Fragment key={condition.key}>
            <div className={isPlainTextConstainer}>
              <FontAwesomeIcon
                className={getIconClass(status)}
                size={iconSize}
                solid={status !== 'degraded'}
              >
                {iconByStatus[status]}
              </FontAwesomeIcon>

              <Text variant={textVariant}>{condition.label}</Text>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default VmhaConditionsStatus

const useStyles = makeStyles<Theme>((theme) => ({
  textContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    gap: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 4,
    padding: theme.spacing(1.5, 1, 1.5, 2),
  },
  plainTextContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  title: {
    marginBottom: theme.spacing(0.5),
  },
  conditionsGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  successIcon: {
    color: theme.palette.green[700],
  },
  failedIcon: {
    color: theme.palette.pink[200],
  },
  degradedIcon: {
    color: theme.palette.orange[500],
  },
  loadingIcon: {
    color: theme.palette.blue[700],
  },
  pointerIcon: {
    color: theme.palette.grey[400],
    fontSize: 8,
  },
  tooltipIconContainer: {
    display: 'grid',
    gridAutoFlow: 'row',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  conditionContainer: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1.5),

    '& p': {
      color: theme.palette.grey[300],
    },
  },
  textWithPointersIcon: {
    fontSize: 8,
    cursor: 'default',
  },
}))
